import {
  Button,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@growdash/design-system/atoms';
import PageWithTitle from '@growdash/growintel/components/hocs/with-title';
import PageFooter from '@growdash/growintel/components/molecules/page-footer';
import SelectBrand from '@growdash/growintel/components/molecules/select-brand';
import { Brand, UniqueMenuItem } from '@growdash/growintel/generated';
import { useGrowintelService } from '@growdash/growintel/hooks/useGrowintelService';
import useTabState from '@growdash/growintel/hooks/useTabState';
import useWatchlist from '@growdash/growintel/hooks/useWatchlist';
import AishaComparisonView from '@growdash/growintel/pages/compare/components/aisha-comparison-view';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import AnalyticsRadarView from './components/analytics-radar-view';
import LocationCoverageView from './components/location-coverage-view';
import MenuComparisonView from './components/menu-comparison-view';
import CompareOnboardingDialog from './components/onboarding-dialog';

type Props = {};

export default function CompareView({}: Props) {
  const service = useGrowintelService();
  const { filters, updateTabState } = useTabState('compare');
  const { refetchWatchlist, isWatchlisted } = useWatchlist();
  const [tempFirstBrand, setTempFirstBrand] = useState<Brand | null>(
    filters?.firstBrand ?? null,
  );
  const [tempSecondBrand, setTempSecondBrand] = useState<Brand | null>(
    filters?.secondBrand ?? null,
  );

  // Synchronize temp states with filters when they change
  useEffect(() => {
    setTempFirstBrand(filters?.firstBrand ?? null);
  }, [filters?.firstBrand]);

  useEffect(() => {
    setTempSecondBrand(filters?.secondBrand ?? null);
  }, [filters?.secondBrand]);

  const handleUpdatefilter = () => {
    updateTabState({
      filters: {
        firstBrand: tempFirstBrand,
        secondBrand: tempSecondBrand,
      },
    });
  };

  const { data: firstBrandData, isFetching: isFirstBrandLoading } = useQuery({
    queryKey: ['compare-first-brand', filters.firstBrand?.id],
    queryFn: async (): Promise<{
      brand: Brand | undefined;
      items: UniqueMenuItem[] | undefined;
    } | null> => {
      const brandId = filters.firstBrand?.id;
      if (!brandId) return null;
      const response =
        await service?.menuItemsAPI.getUniqueMenuItemsMenuItemsUniqueGet(
          brandId,
          1,
          Number.MAX_SAFE_INTEGER,
        );
      const brandResponse =
        await service?.brandsAPI.getBrandBrandsIdGet(brandId);
      return {
        brand: brandResponse?.data,
        items: response?.data.items,
      };
    },
    retry: false,
  });

  const { data: secondBrandData, isFetching: isSecondBrandLoading } = useQuery({
    queryKey: ['compare-second-brand', filters.secondBrand?.id],
    queryFn: async (): Promise<{
      brand: Brand | undefined;
      items: UniqueMenuItem[] | undefined;
    } | null> => {
      const brandId = filters.secondBrand?.id;
      if (!brandId) return null;

      const itemsResponse =
        await service?.menuItemsAPI.getUniqueMenuItemsMenuItemsUniqueGet(
          brandId,
          1,
          Number.MAX_SAFE_INTEGER,
        );
      const brandResponse =
        await service?.brandsAPI.getBrandBrandsIdGet(brandId);

      return {
        brand: brandResponse?.data,
        items: itemsResponse?.data.items,
      };
    },
    retry: false,
  });

  const isEmptyState = !filters.firstBrand?.id || !filters.secondBrand?.id;

  const brandsLoading = isFirstBrandLoading || isSecondBrandLoading;

  let warningToolTip;
  if (!tempFirstBrand || !tempSecondBrand) {
    warningToolTip = 'Select two brands to see detailed comparison.';
  } else {
    warningToolTip =
      tempFirstBrand?.id === tempSecondBrand?.id
        ? 'Please select different brands to compare'
        : '';
  }

  return (
    <>
      <CompareOnboardingDialog
        tempFirstBrand={tempFirstBrand}
        setTempFirstBrand={setTempFirstBrand}
        tempSecondBrand={tempSecondBrand}
        setTempSecondBrand={setTempSecondBrand}
        onSubmit={handleUpdatefilter}
      />

      <PageWithTitle title="Compare">
        <div className="w-full grid grid-cols-1">
          <div className="max-md:flex-wrap flex items-center gap-4 bg-quarternary rounded-xl p-3 mb-4">
            <SelectBrand
              className="lg:max-w-64"
              onValueChange={setTempFirstBrand}
              value={tempFirstBrand?.id ?? ''}
            />
            <div className=" whitespace-nowrap text-neutral font-medium text-sm max-md:text-center max-md:w-full">
              Compare with
            </div>
            <SelectBrand
              className="lg:max-w-64"
              onValueChange={setTempSecondBrand}
              value={tempSecondBrand?.id ?? ''}
            />
            <Tooltip>
              <TooltipTrigger className="mt-auto">
                <Button
                  onClick={handleUpdatefilter}
                  disabled={!!warningToolTip}
                >
                  Compare
                </Button>
              </TooltipTrigger>

              {!!warningToolTip && (
                <TooltipContent>{warningToolTip}</TooltipContent>
              )}
            </Tooltip>
          </div>

          <div className="w-full grid lg:grid-cols-12 gap-4">
            <div className="lg:col-span-4">
              <AishaComparisonView
                isLoadingBrands={brandsLoading}
                firstBrandData={firstBrandData}
                secondBrandData={secondBrandData}
                isEmptyState={isEmptyState}
              />
              <div className="mt-4">
                <AnalyticsRadarView
                  isLoading={brandsLoading}
                  firstBrandData={firstBrandData?.brand}
                  secondBrandData={secondBrandData?.brand}
                  isEmptyState={isEmptyState}
                />
              </div>
            </div>
            <div className="lg:col-span-8">
              <div>
                {brandsLoading ? (
                  <div className="flex gap-4">
                    <div className="w-full">
                      <Skeleton className="w-full h-48" />
                      <Skeleton className="w-full h-96 mt-4" />
                    </div>
                    <div className="w-full">
                      <Skeleton className="w-full h-48" />
                      <Skeleton className="w-full h-96 mt-4" />
                    </div>
                  </div>
                ) : (
                  <>
                    <MenuComparisonView
                      isEmptyState={isEmptyState}
                      secondBrandData={secondBrandData}
                      firstBrandData={firstBrandData}
                      isLoadingBrands={brandsLoading}
                      refetchWatchlist={refetchWatchlist}
                      isWatchlisted={isWatchlisted}
                    />
                    <LocationCoverageView />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        <PageFooter currentPage="compare" />
      </PageWithTitle>
    </>
  );
}
